import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useContext
} from "react";
import { Trans, t, Plural } from "@lingui/macro";
import PropTypes from "prop-types";
import { useHistory, useLocation, useParams } from "react-router-dom";
import shallow from "zustand/shallow";
import { i18nMark } from "@lingui/react";

import { Classes } from "base/classes";
import { Modal } from "widgets/modal";
import { UserIcon, AVATAR_SIZE, Icon, Collage } from "widgets/datadisplay";
import { useAccountStore, useUserStore } from "base/stores";
import { getFormattedDate, generateId } from "base/utils";
import { BUTTON_TYPES } from "widgets/inputs";
import { BannerNotification, Alert, Toast, Skeleton } from "widgets/feedback";
import { EmptyState, EMPTY_STATE_SIZES, ErrorBoundary } from "widgets/layouts";
import { hasAccess } from "widgets/others";
import { ReadonlyEditor, FORMAT_TYPES } from "widgets/richtext";
import { Popover, POPOVER_POSITIONS } from "widgets/popover";
import { i18n } from "base/i18n";
import { InfiniteScrollList } from "widgets/infinitescroll";

import { Embedurl } from "../post/embedurl";
import {
  CHANNEL_ROLES,
  DATE_FORMAT_LONG,
  DOWNVOTE,
  UPVOTE,
  CHANNEL_CONST,
  PAGE_SIZE
} from "@/common/utils/constant";
import {
  voteDetails,
  getTopicContents,
  getVoteCount,
  getEditedContent,
  onIntersection,
  onUpdateBookmark,
  updateTopicPin
} from "@/common/utils/utils";
import { TopicActions } from "./topic.actions";
import { CommentSection } from "./comment.section";
import { CommentBox } from "./comment.box";
import { Quote } from "./quote";
import { useChannelStore } from "@/common/store/channel.store";
import { ForumService } from "@/common/service/forum.service";
import { useForumStore } from "@/common/store/forum.store";
import { CreateTopic } from "./create.topic";
import { SkeletonLoader } from "../skeleton_loader";
import { PostViewsModal } from "../reaction/post.views.modal";
import { usePostStore } from "@/common/store/post.store";
import { RemovePostModal } from "../post/remove.post";
import { Mention } from "../post/mention";
import { SpaceContext } from "@/container/forum";

import styles from "../../forum/forum.css";

import HorizontalMoreIcon from "../../icons/TopicOptions.svg";
import CloseIcon from "../../icons/CloseModal.svg";
import EditIcon from "@/common/icons/Edit.svg";
import DeleteIcon from "@/common/icons/Delete.svg";
import LinkIcon from "@/common/icons/Link.svg";
import NotificationIcon from "@/common/icons/Notification.svg";
import NotificationMuteIcon from "@/common/icons/NotificationMute.svg";
import MarkIcon from "@/common/icons/ClosedTopic.svg";
import SolvedIcon from "@/common/icons/Solved.svg";
import LockIcon from "@/common/icons/Lock.svg";
import NoItemFoundIcon from "@/common/icons/NoItemFound.svg";
import PinIcon from "@/common/icons/Pintopic.svg";
import UnpinIcon from "@/common/icons/Unpintopic.svg";

const TURN_NOTIFICATION = {
  true: i18nMark("Turn off notifications"),
  false: i18nMark("Turn on notifications")
};
const TURN_CLOSE = {
  true: i18nMark("Reopen topic"),
  false: i18nMark("Mark as closed")
};
const TURN_SOLVE = {
  true: i18nMark("Mark as unsolved"),
  false: i18nMark("Mark as solved")
};

const { showConfirm } = Alert;
const { showSuccess } = Toast;

export function TopicDetail({
  teamId,
  listTopicId,
  closeModalHandler,
  fromList = false,
  isVertical = false,
  noResults = false,
  bookmarks,
  fromModal = false,
  searchTerm,
  activeTabId,
  showBooksMarks
}) {
  const history = useHistory();
  const topicRef = useRef();
  const currentPage = useRef(null);
  const pageNumber = useRef(2);
  const endList = useRef(true);
  let { search, state = {} } = useLocation();
  let { topicId = listTopicId, commentId } = useParams();

  const { isListView, spaceRoute } = useContext(SpaceContext);

  const TopicDetailWrapper = isVertical ? Fragment : Modal;

  const { UserDetails } = useAccountStore.getState();
  const { currentUser } = useUserStore.getState();
  const { mediumId, postCount, roles } = useChannelStore(
    (state) => ({
      mediumId: state.mediumId,
      postCount: state.postCount,
      roles: state.roles
    }),
    shallow
  );
  const topicInfo = useForumStore(
    // eslint-disable-next-line kf/hook-func-name
    useCallback((state) => state.topicData[topicId], [topicId])
  );

  const {
    batchTopicDetails = {},
    isCurrentTopicDeleted,
    pinnedTopicIds = []
  } = useForumStore(
    (state) => ({
      batchTopicDetails: state.batchTopicDetails,
      isCurrentTopicDeleted: state.isCurrentTopicDeleted,
      pinnedTopicIds: state.pinnedTopicIds
    }),
    shallow
  );

  const [comments, setComments] = useState(
    batchTopicDetails[topicId] ? batchTopicDetails[topicId].Comments : []
  );
  const [totalComments, setTotalComments] = useState(
    batchTopicDetails[topicId] ? batchTopicDetails[topicId].TotalComments : []
  );
  const [topicData, setTopicData] = useState(topicInfo ? topicInfo : {});
  const [reaction, setReactions] = useState({});
  const [selectedCommentId, setSelectedCommentId] = useState(commentId);
  const [isShowCreateModal, setShowCreateModal] = useState(false);
  const [voteCount, setVoteCount] = useState(0);
  const [highlightComment, setHighlightComment] = useState(false);
  const [isGetNotificationSettingCalled, setIsGetNotificationSettingCalled] =
    useState(false);
  const [isNotificationsOn, setIsNotificationsOn] = useState(false);
  const [isClosedTopic, setClosedTopic] = useState(false);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [isLoadingTopic, setLoadingTopic] = useState(false);
  const [isLoadingComments, setLoadingComments] = useState(true);
  const [isLoadingMoreComments, setLoadingMoreComments] = useState(false);
  const [focus, setEditorFocus] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [commentsList, setCommentsList] = useState([]);
  const [isShowViewComments, setShowViewComments] = useState(false);
  const [deleteTopic, setDeleteTopic] = useState(false);
  const [isShowingSelectedComment, setShowingSelectedComment] = useState(false);
  const [notifyCommentId, setNotifyComment] = useState();

  const isEdit = new URLSearchParams(search).get("isEdit");

  const { heading, topicContent } = useMemo(
    function getContents() {
      const [heading, topicContent] = getTopicContents(topicData);
      return {
        heading,
        topicContent: getEditedContent(topicData.IsEdited, topicContent)
      };
    },
    [topicData]
  );
  const isCreator = useMemo(
    function setCreator() {
      return topicData._created_by?._id === currentUser?._id;
    },
    [topicData, currentUser]
  );
  const { isUpvoted, isDownvoted } = useMemo(
    function getVoteDetails() {
      return voteDetails(reaction, currentUser._id);
    },
    [reaction, currentUser._id]
  );

  const bestSolution = useMemo(
    function getBestSolution() {
      if (!topicData.BestSolution) return null;
      return comments.find((comment) => comment._id === topicData.BestSolution);
    },
    [comments, topicData.BestSolution]
  );

  const isPinnedTopic = useMemo(
    function isPinned() {
      return pinnedTopicIds.includes(topicData._id);
    },
    [pinnedTopicIds, topicData._id]
  );

  const isAdmin = hasAccess({
    roles: [CHANNEL_ROLES.ADMIN, CHANNEL_ROLES.MODERATOR],
    flowRoles: roles
  });

  useEffect(
    function updateTopicDetails() {
      if (topicInfo) {
        setTopicData(topicInfo);
      }
    },
    [topicInfo]
  );

  useEffect(
    function updateTopicDetails() {
      if (batchTopicDetails) {
        setComments(
          batchTopicDetails[topicId] ? batchTopicDetails[topicId].Comments : []
        );
        setTotalComments(
          batchTopicDetails[topicId]
            ? batchTopicDetails[topicId].TotalComments
            : []
        );
      }
    },
    [batchTopicDetails, topicId]
  );

  useEffect(
    function setDetails() {
      const reactions = batchTopicDetails[topicData._id]?.Reactions?.find(
        (reaction) => reaction._id === currentUser._id
      );
      setReactions(reactions);
    },
    [batchTopicDetails, topicData._id, currentUser._id]
  );

  useEffect(
    function getSetings() {
      getNotificationSetting();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [topicData]
  );

  // eslint-disable-next-line kf/hook-func-name
  useEffect(
    function updateNotifyComment() {
      if (state.notificationData && state.notificationData.length) {
        setNotifyComment(state.notificationData[0].Properties.CommentId);
      }
    },
    [state]
  );

  useEffect(
    function setModalVisibility() {
      setShowCreateModal(!!isEdit);
    },
    [isEdit]
  );

  useEffect(
    function setClose() {
      setClosedTopic(topicData.IsClosed);
    },
    [topicData.IsClosed]
  );
  useEffect(
    function setClose() {
      setVoteCount(topicData.VoteCount || 0);
    },
    [topicData.VoteCount]
  );
  useEffect(
    function getPostDetails() {
      if (topicId) {
        useForumStore.getState().setCurrentTopicId(topicId);
        if (topicData._id) {
          const [commentPromise] = ForumService.getComments(
            teamId,
            mediumId,
            topicId,
            1,
            PAGE_SIZE
          );
          commentPromise().then((result) => {
            endList.current = result.Comments.length === PAGE_SIZE;
            useForumStore.getState().setCommentDetails(topicId, result);
            setLoadingComments(false);
          });
          return;
        }
        setLoadingTopic(true);
        const [getTopicPromise] = ForumService.getTopicDetails(
          teamId,
          mediumId,
          topicId
        );
        getTopicPromise().then((result) => {
          setLoadingTopic(false);
          useForumStore.getState().setSingleTopicDetails(result);
          const [commentPromise] = ForumService.getComments(
            teamId,
            mediumId,
            topicId,
            1,
            PAGE_SIZE
          );
          commentPromise().then((result) => {
            endList.current = result.Comments.length === PAGE_SIZE;
            useForumStore.getState().setCommentDetails(topicId, result);
            setLoadingComments(false);
          });
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [topicId]
  );

  useEffect(
    function setBestSolution() {
      if (topicData.BestSolution && comments.length > 0) {
        let bestComment = comments.find(
          (comment) => comment._id === topicData.BestSolution
        );
        useForumStore.getState().setBestComment(bestComment);
      }
    },
    [topicData, comments]
  );

  useEffect(
    function getComment() {
      const id = selectedCommentId || notifyCommentId;
      if (isShowingSelectedComment) {
        const [getSingleComment] = ForumService.getSingleComment(
          teamId,
          mediumId,
          topicId,
          id
        );
        getSingleComment()
          .then((result) => {
            setCommentsList([result.Comment]);
          })
          .catch((error) => {
            if (error && error.error_code === "KISSFLOW_ERROR_06501") {
              setCommentsList([{ _id: id, isDeleted: true }]);
            }
          });
        setTimeout(() => {
          const commentElement = document.getElementById(id);
          if (commentElement) {
            commentElement.scrollIntoView();
            setHighlightComment(true);
          }
        }, 1000);
      } else {
        setAllComments();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [bestSolution, comments]
  );

  // eslint-disable-next-line kf/hook-func-name
  useEffect(
    function updateViewComments() {
      setShowViewComments(
        !!(totalComments > 1 && (selectedCommentId || notifyCommentId))
      );
      setShowingSelectedComment(
        (selectedCommentId || notifyCommentId) && pageNumber.current === 2
      );
    },
    [totalComments, selectedCommentId, notifyCommentId]
  );

  useEffect(function resetState() {
    return () => {
      useForumStore.getState().setSingleTopicDetails({});
      useForumStore.getState().setBestComment(null);
      useForumStore.getState().resetCommentData(topicId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isDisableVote = useMemo(
    function enable() {
      return (
        isClosedTopic ||
        isCurrentTopicDeleted ||
        topicData._created_by?._id === currentUser._id
      );
    },
    [isClosedTopic, isCurrentTopicDeleted, topicData, currentUser]
  );

  const setAllComments = useCallback(
    function getComments() {
      let allComments = comments || [];
      setCommentsList(allComments);
    },
    [comments]
  );

  const plugin = useMemo(function getPlugin() {
    return {
      [FORMAT_TYPES.EMBED_LINK]: (prop) => {
        return (
          <Embedurl
            node={prop.node.data}
            postId={topicData?._id}
            readOnly={true}
          />
        );
      },
      [FORMAT_TYPES.COLLAGE]: (prop) => {
        return <Collage innerRef={{}} node={prop.node.data} readOnly={true} />;
      },
      [FORMAT_TYPES.MENTION]: (prop) => {
        return <Mention node={prop.node.data} teamId={teamId} />;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(
    function checkIntersection() {
      if (topicData._id) {
        const { currentUser } = useUserStore.getState();
        let [isCurrentPostViewed] = usePostStore
          .getState()
          .getViewedPost(currentUser._id, topicData._id);
        const target = topicRef.current;
        if (
          target &&
          !isCurrentPostViewed &&
          currentUser._id !== topicData._created_by?._id
        ) {
          setTimeout(() => {
            onIntersection(teamId, mediumId, topicData._id, currentUser, true);
          }, 3000);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [topicData]
  );

  function getParentComment(parentCommentId) {
    return comments.find((comment) => comment._id === parentCommentId);
  }

  function postVote(voteType) {
    if (!isDisableVote) {
      const eventId = generateId("Ev");
      useForumStore.getState().setEventId(eventId);
      const data = {
        ReactionType: voteType,
        EventId: eventId
      };

      if (
        (isUpvoted && voteType === UPVOTE) ||
        (isDownvoted && voteType === DOWNVOTE)
      ) {
        const [removeVotePromise] = ForumService.removeVote(
          teamId,
          mediumId,
          topicData._id,
          { EventId: eventId }
        );
        removeVotePromise().catch((error) => {
          const reactions = batchTopicDetails[topicData._id]?.Reactions?.find(
            (reaction) => reaction._id === currentUser._id
          );
          setReactions(reactions);
        });
        setReactions({});
        const reactions = batchTopicDetails[topicData._id]?.Reactions?.filter(
          (reaction) => reaction._id === currentUser._id
        );
        useForumStore.getState().updateTopicVote(topicId, null, reactions);
      } else {
        const [postVotePromise] = ForumService.postVote(
          teamId,
          mediumId,
          topicData._id,
          data
        );
        postVotePromise().catch(() => {
          const reactions = batchTopicDetails[topicData._id]?.Reactions?.find(
            (reaction) => reaction._id === currentUser._id
          );
          setReactions(reactions);
        });
        const reactions = {
          _id: currentUser._id,
          ReactionType: voteType
        };
        setReactions(reactions);
        useForumStore.getState().updateTopicVote(topicId, null, reactions);
      }
      let newVoteCount = getVoteCount(
        isUpvoted,
        isDownvoted,
        voteType,
        voteCount
      );
      setVoteCount(newVoteCount);
      useForumStore.getState().updateTopicVote(topicId, newVoteCount);
    }
  }

  function onEditTopic() {
    setShowCreateModal(true);
    history.push({ search: "?isEdit=true" });
    closePopover();
  }

  function getNotificationSetting() {
    if (!isGetNotificationSettingCalled && topicData._id) {
      let [getNotification] = ForumService.getNotificationSetting(
        topicData.Medium,
        topicData.MediumId,
        topicData._id
      );
      getNotification().then(
        (data) => {
          setIsGetNotificationSettingCalled(true);
          let isNotificationsOnSetting = true;
          if (data && Object.keys(data).length > 0) {
            isNotificationsOnSetting = !!data.Setting;
            setIsNotificationsOn(isNotificationsOnSetting);
          }
        },
        (err) => {
          console.info("get notification error", err);
        }
      );
    }
  }

  function onSwitchNotification() {
    let [updateNotificationSettingResponse] =
      ForumService.updateNotificationSetting(
        topicData.Medium,
        topicData.MediumId,
        topicData._id,
        !isNotificationsOn
      );
    updateNotificationSettingResponse().then(
      (result) => {
        setIsNotificationsOn((state) => !state);
      },
      (err) => {
        console.info("notification error ", err);
      }
    );
    closePopover();
  }

  function switchTopicClose() {
    const data = {
      IsClosed: !isClosedTopic
    };
    const [closeTopicPromise] = ForumService.updateTopic(
      teamId,
      mediumId,
      topicData._id,
      data
    );
    closeTopicPromise().then(() => {
      setClosedTopic(!isClosedTopic);
      useForumStore.getState().closeTopic(topicData._id, isClosedTopic);
    });
    closePopover();
  }

  function switchTopicSolve() {
    const data = {
      IsSolved: !topicData.IsSolved
    };
    const [solveTopicPromise] = ForumService.updateTopic(
      teamId,
      mediumId,
      topicData._id,
      data
    );
    solveTopicPromise();
    closePopover();
  }

  function showDeleteTopic(value) {
    setDeleteTopic(value);
  }

  function deleteTopicModal(data) {
    const [deleteTopicPromise] = ForumService.deleteTopic(
      teamId,
      mediumId,
      topicData._id,
      data
    );
    deleteTopicPromise().then(() => {
      history.push(`/${spaceRoute}/${teamId}/channel/${mediumId}`);
      useChannelStore.getState().updatePostCount(postCount - 1);
    });
  }

  function onDelete() {
    showConfirm({
      title: i18n._(t`Delete topic`),
      content: i18n._(t`Are you sure you want to delete?`),
      okText: i18n._(t`Delete`),
      cancelText: i18n._(t`Cancel`),
      onOK: () => {
        deleteTopicModal();
      },
      okButtonType: BUTTON_TYPES.ERROR
    });
  }

  function closePopover() {
    setIsPopoverOpen(false);
  }

  function updateTopicDetails(result) {
    useForumStore.getState().updateTopicDetails(result);
  }

  function copyTopic() {
    let hostName = window.location.host;
    navigator.clipboard.writeText(
      // eslint-disable-next-line max-len
      `https://${hostName}/view/${spaceRoute}/${teamId}/${topicData.Medium}/${topicData.MediumId}/topic/${topicData._id}`
    );
    showSuccess({
      message: i18n._(t`Link copied`)
    });
    closePopover();
  }

  function onPinTopic() {
    updateTopicPin({
      topicDataId: topicData._id,
      isPinnedTopic,
      teamId,
      mediumId,
      activeTabId
    });
    closePopover();
  }

  function onCloseModal() {
    if (fromList) closeModalHandler();
    if (!listTopicId) {
      history.push(`/${spaceRoute}/${teamId}/channel/${mediumId}`);
    }
    useForumStore.getState().setQuotedText([]);
    useForumStore.getState().setReplyComment({});
    closePopover();
  }

  function fetchMoreData() {
    if (currentPage.current === pageNumber.current) return;
    currentPage.current = pageNumber.current;
    if (endList.current) {
      setLoadingMoreComments(true);
      const [commentPromise] = ForumService.getComments(
        teamId,
        mediumId,
        topicId,
        pageNumber.current,
        PAGE_SIZE
      );
      commentPromise()
        .then((result) => {
          endList.current = result.Comments?.length === PAGE_SIZE;
          pageNumber.current = pageNumber.current + 1;
          currentPage.current = null;
          useForumStore.getState().setMoreCommentDetails(topicId, result);
          setLoadingMoreComments(false);
        })
        .catch((err) => console.info(err, "get comment"));
    }
  }

  function onShowAllComments() {
    setAllComments();
    setShowViewComments(false);
    setHighlightComment(false);
    setSelectedCommentId(null);
    setNotifyComment(null);
    setShowingSelectedComment(false);
  }

  function showAddSuccess() {
    showSuccess({ message: i18n._(t`The topic was added to your bookmarks`) });
  }
  function showRemoveSuccess() {
    showSuccess({
      message: i18n._(t`The topic was removed to your bookmarks`)
    });
  }

  if (topicData._is_deleted) return null;

  return (
    <ErrorBoundary>
      <TopicDetailWrapper
        open={topicId}
        closable={false}
        width="92rem"
        height="100vh"
        backgroundClassName={styles.createTopic}
        footerRenderer={() => null}
      >
        {!noResults ? (
          <div className={styles.detailedView} ref={topicRef}>
            {!isLoadingComments && (
              <TopicActions
                postVote={postVote}
                isUpvoted={isUpvoted}
                isDisableVote={isDisableVote}
                voteCount={voteCount}
                isDownvoted={isDownvoted}
                setEditorFocus={setEditorFocus}
                totalComments={totalComments}
                setShowViewModal={setShowViewModal}
                topicData={topicData}
                onUpdateBookmark={onUpdateBookmark}
                teamId={teamId}
                showRemoveSuccess={showRemoveSuccess}
                showAddSuccess={showAddSuccess}
                fromDetail
              />
            )}
            <div className={`${styles.options} ${styles.unselection}`}>
              <div
                className={`${styles.topOptions} ${styles.flexBox} ${
                  isListView ? styles.listView : ""
                }`}
              >
                <div className={`${styles.left}`}>
                  <UserIcon
                    size={AVATAR_SIZE.SIZE44}
                    userId={topicData._created_by?._id}
                    name={topicData._created_by?.Name}
                  />
                  <div>
                    <div className={`${Classes.baseplus600}`}>
                      {topicData._created_by?.Name}
                    </div>
                    <div className={`${Classes.small}`}>
                      {" "}
                      {getFormattedDate(
                        topicData._created_at,
                        DATE_FORMAT_LONG
                      )}
                    </div>
                  </div>
                </div>
                <div className={styles.right}>
                  <Popover
                    isOpen={isPopoverOpen}
                    position={POPOVER_POSITIONS.BOTTOM_RIGHT}
                    onOpen={() => setIsPopoverOpen(true)}
                    onClose={() => {
                      closePopover();
                    }}
                    targetRenderer={() => {
                      return (
                        <div
                          className={`${styles.moreIcon} ${
                            fromModal ? styles.moreIconModal : ""
                          }`}
                          onClick={(e) => {
                            e.stopPropagation();
                          }}
                        >
                          <Icon>
                            <HorizontalMoreIcon />
                          </Icon>
                        </div>
                      );
                    }}
                  >
                    <div className={`${styles.moreMenuOption} `}>
                      {isCreator && !isClosedTopic && !isCurrentTopicDeleted && (
                        <div
                          className={`base ${styles.item}`}
                          onClick={onEditTopic}
                        >
                          <EditIcon />
                          <span className={Classes.baseplus400}>
                            <Trans>Edit</Trans>
                          </span>
                        </div>
                      )}
                      {isAdmin && (
                        <div
                          className={`base ${styles.item}`}
                          onClick={onPinTopic}
                        >
                          {isPinnedTopic ? <UnpinIcon /> : <PinIcon />}
                          <span className={Classes.baseplus400}>
                            {isPinnedTopic ? (
                              <Trans>Unpin this topic</Trans>
                            ) : (
                              <Trans>Pin this topic</Trans>
                            )}
                          </span>
                        </div>
                      )}
                      <div
                        className={`base ${styles.item}`}
                        onClick={copyTopic}
                      >
                        <LinkIcon />
                        <span className={Classes.baseplus400}>
                          <Trans>Copy topic link</Trans>
                        </span>
                      </div>
                      <div
                        className={`base ${styles.item}`}
                        onClick={onSwitchNotification}
                      >
                        {isNotificationsOn ? (
                          <NotificationMuteIcon />
                        ) : (
                          <NotificationIcon />
                        )}
                        <span className={Classes.baseplus400}>
                          {i18n._(TURN_NOTIFICATION[isNotificationsOn])}
                        </span>
                      </div>
                      {isCreator && (
                        <>
                          <div
                            className={`base ${styles.item}`}
                            onClick={switchTopicClose}
                            data-heaplocator={
                              isClosedTopic
                                ? "forum-topic-reopen"
                                : "forum-topic-closed"
                            }
                          >
                            {isClosedTopic ? <LockIcon /> : <MarkIcon />}
                            <span className={Classes.baseplus400}>
                              {i18n._(TURN_CLOSE[isClosedTopic || false])}
                            </span>
                          </div>
                          <div
                            className={`base ${styles.item}`}
                            onClick={switchTopicSolve}
                            data-heaplocator={
                              topicData.IsSolved
                                ? "forum-topic-unsolve"
                                : "forum-topic-solve"
                            }
                          >
                            {topicData.IsSolved ? (
                              <LockIcon fill="var(--icon-dark-gray)" />
                            ) : (
                              <SolvedIcon />
                            )}
                            <span className={Classes.baseplus400}>
                              {i18n._(TURN_SOLVE[topicData.IsSolved || false])}
                            </span>
                          </div>
                          <div
                            className={`base ${styles.item} ${styles.danger}`}
                            onClick={onDelete}
                          >
                            <DeleteIcon fill="var(--secondary-red-500)" />
                            <span>
                              <Trans>Delete topic</Trans>
                            </span>
                          </div>
                        </>
                      )}
                      {!isCreator &&
                        hasAccess({
                          roles: [CHANNEL_ROLES.ADMIN],
                          flowRoles: roles
                        }) && (
                          <div
                            className={`base ${styles.item} ${styles.danger}`}
                            onClick={() => {
                              showDeleteTopic(true);
                              closePopover();
                            }}
                          >
                            <DeleteIcon />
                            <span>
                              <Trans>Remove topic</Trans>
                            </span>
                          </div>
                        )}
                    </div>
                  </Popover>
                  {!isVertical && (
                    <div className={styles.closeIcon} onClick={onCloseModal}>
                      <CloseIcon height={20} width={20} />
                    </div>
                  )}
                </div>
              </div>
              <Skeleton
                active={true}
                loading={isLoadingTopic}
                paragraph={{ rows: 1, width: ["70%"] }}
                title={false}
                className={styles.topicSkeleton}
              >
                <div
                  className={`${Classes.h3600} ${styles.heading} ${
                    isVertical ? styles.verticalHeading : ""
                  }`}
                >
                  {heading?.[0]?.nodes?.[0]?.text}
                </div>
              </Skeleton>
            </div>
            <div
              className={`${styles.topicContent} ${
                isListView ? styles.listView : ""
              } ${fromModal ? styles.topicContentModal : ""}`}
              id="topic-content"
            >
              <InfiniteScrollList
                className={styles.commentScroller}
                onEndReached={() => fetchMoreData()}
                isLoading={isLoadingComments || isLoadingMoreComments}
                endOffset={50}
                loaderRenderer={() => <SkeletonLoader justifyContent="left" />}
              >
                <Skeleton
                  active={true}
                  loading={isLoadingTopic}
                  paragraph={{ rows: 3, width: ["50%", "50%", "30%"] }}
                  title={false}
                  className={styles.contentSkeleton}
                >
                  {!isClosedTopic && <Quote setEditorFocus={setEditorFocus} />}
                  <ReadonlyEditor
                    key={topicId}
                    value={topicContent}
                    renderer={plugin}
                    className={`${styles.topicDetailContent} ${Classes.baseplus400} ${styles.richtexteditor}`}
                  />
                </Skeleton>
                {isClosedTopic && (
                  <div className={styles.banner}>
                    <BannerNotification customIcon={<LockIcon />}>
                      <Trans>
                        This discussion is no longer open for new replies.
                      </Trans>
                    </BannerNotification>
                  </div>
                )}
                {isCurrentTopicDeleted && (
                  <div className={styles.banner}>
                    <BannerNotification>
                      <Trans>This discussion has been deleted</Trans>
                    </BannerNotification>
                  </div>
                )}
                <div className={styles.comments}>
                  {bestSolution && (
                    <CommentSection
                      comment={bestSolution}
                      topicId={topicId}
                      key={`topicComment-${topicId}`}
                      getParentComment={getParentComment}
                      bestCommentId={topicData.BestSolution}
                      index={0}
                      setEditorFocus={setEditorFocus}
                      isClosedTopic={isClosedTopic}
                      highlightComment={highlightComment}
                      commentId={selectedCommentId || notifyCommentId}
                      teamId={teamId}
                      isShowViewComments={isShowViewComments}
                    />
                  )}
                  {isShowViewComments && (
                    <span
                      className={`${styles.linkText} smallplus`}
                      onClick={onShowAllComments}
                    >
                      <Trans> View all replies</Trans>
                    </span>
                  )}
                  {commentsList && commentsList.length > 1 && (
                    <span className={`small`}>
                      <Plural
                        value={commentsList.length}
                        one={"# reply"}
                        other={"# replies"}
                      />
                    </span>
                  )}
                  {commentsList &&
                    commentsList.length > 0 &&
                    commentsList.map((comment, index) =>
                      !comment.isDeleted ? (
                        <CommentSection
                          comment={comment}
                          topicId={topicId}
                          key={`topicComment-${index}`}
                          getParentComment={getParentComment}
                          index={index}
                          setEditorFocus={setEditorFocus}
                          isClosedTopic={isClosedTopic}
                          highlightComment={highlightComment}
                          commentId={selectedCommentId || notifyCommentId}
                          teamId={teamId}
                          isShowViewComments={isShowViewComments}
                        />
                      ) : (
                        <React.Fragment>
                          <div>
                            <div className={styles.deletedComment}>
                              <BannerNotification>
                                <span>
                                  <Trans>This comment has been deleted</Trans>
                                </span>
                              </BannerNotification>
                            </div>
                          </div>
                        </React.Fragment>
                      )
                    )}
                  {!isClosedTopic &&
                    !isLoadingComments &&
                    !isCurrentTopicDeleted &&
                    !isShowingSelectedComment && (
                      <div className={styles.commentBoxSection}>
                        <UserIcon
                          size={AVATAR_SIZE.SIZE44}
                          userId={UserDetails._id}
                          name={UserDetails.Name}
                        />
                        <CommentBox
                          focus={focus}
                          setEditorFocus={setEditorFocus}
                          mediumId={mediumId}
                          topicId={topicId}
                          teamId={teamId}
                        />
                      </div>
                    )}
                </div>
              </InfiniteScrollList>
            </div>
          </div>
        ) : (
          <Fragment>
            {noResults && searchTerm && (
              <div className={styles.emptyState}>
                <EmptyState
                  img={<NoItemFoundIcon />}
                  text={i18n._(
                    t`We couldn’t find any related information. Please try another search.`
                  )}
                  size={EMPTY_STATE_SIZES.LARGE}
                />
              </div>
            )}
            {noResults && showBooksMarks && !bookmarks && (
              <div className={`${styles.bookmarkEmpty}`}>
                <EmptyState
                  img={<NoItemFoundIcon />}
                  text={i18n._(t`There are no bookmarks yet`)}
                  size={EMPTY_STATE_SIZES.LARGE}
                />
              </div>
            )}
          </Fragment>
        )}

        <CreateTopic
          setShowCreateModal={setShowCreateModal}
          isShowCreateModal={isShowCreateModal}
          isEdit={!!isEdit}
          topicData={topicData}
          updateTopicDetails={updateTopicDetails}
          teamId={teamId}
        />
        {showViewModal && (
          <PostViewsModal
            title={
              <Plural
                value={topicData.SeenCount}
                one="# member viewed this topic"
                other="# members viewed this topic"
              />
            }
            onClose={() => setShowViewModal(false)}
            medium={CHANNEL_CONST.CHANNEL}
            mediumId={mediumId}
            instanceId={topicData._id}
            teamId={teamId}
          />
        )}
        {deleteTopic && (
          <RemovePostModal
            showRemovePost={showDeleteTopic}
            removePostModal={deleteTopicModal}
            title={i18n._(`Remove topic`)}
          />
        )}
      </TopicDetailWrapper>
    </ErrorBoundary>
  );
}

TopicDetail.propTypes = {
  fromList: PropTypes.bool,
  activeTabId: PropTypes.string,
  listTopicId: PropTypes.string,
  closeModalHandler: PropTypes.func,
  teamId: PropTypes.string,
  isVertical: PropTypes.bool,
  noResults: PropTypes.bool,
  fromModal: PropTypes.bool,
  bookmarks: PropTypes.number,
  searchTerm: PropTypes.string,
  showBooksMarks: PropTypes.bool
};
